import React, { FunctionComponent, useContext } from "react";
import { useTranslate, $$ } from "../../helpers";
import { Card } from "@cmsgov/ds-medicare-gov";
import { AppStore } from "../../app/store";
import { FormattedMessage } from "react-intl-phraseapp";
import { CollapsibleBox, CheckmarkIcon } from "../../components";
import PlanDataUtils from "../../helpers/planDataUtils";
import { TooltipWithClose } from "../../components";

const OriginalMedicarePlanCard: FunctionComponent = () => {
  const t = useTranslate();
  const { state } = useContext(AppStore.AppContext);

  const accessibilitySpan = (available: boolean): JSX.Element => (
    <span className="ds-u-visibility--screen-reader">
      {available
        ? ` ${t("plan_card.benefit.is_available")}`
        : ` ${t("plan_card.benefit.is_not_available")}`}
    </span>
  );

  const partAPremium = "$0";
  const partBPremium = $$(PlanDataUtils.getPartBPremiumForYear(state.year));
  const healthDeductible = $$(
    PlanDataUtils.getPartBHealthDeductibleForYear(state.year)
  );

  return (
    <li
      aria-labelledby="original-medicare-heading"
      className="ds-u-margin-bottom--4"
    >
      <Card className="PlanCard">
        <div className="ds-u-margin-bottom--3">
          <h2 id="original-medicare-heading" className="PlanCard__header">
            <FormattedMessage
              id="om.card_title"
              values={{
                year: state.year,
              }}
            />
          </h2>
        </div>
        <div className="PlanCard__body">
          <div>
            <div className="PlanCard__flex_left">
              <div>
                <div className="PlanCard__section" data-testid="monthlyPremium">
                  <h3 className="PlanCard__section_heading">
                    {t("plan_card.section_heading.monthly_premium")}
                  </h3>
                  <div className="PlanCard__info_group">
                    <div className="PlanCard__data">{partAPremium}</div>
                    <div className="PlanCard__line_items">
                      <p>{t("om_card.part_a_premium")}</p>
                      <p>
                        {t("plan_card.does_not_include")}:{" "}
                        <FormattedMessage
                          id="plan_card.amount_partb_premium"
                          values={{
                            amount: partBPremium,
                          }}
                        />
                      </p>
                    </div>
                  </div>
                </div>
                <div className="PlanCard__section" data-testid="otherCosts">
                  <h3 className="PlanCard__section_heading">
                    {t("plan_card.section_heading.other_costs")}
                  </h3>
                  <div className="PlanCard__info_group">
                    <span className="PlanCard__data">{healthDeductible}</span>
                    <TooltipWithClose
                      title={t("plan_card.tooltip.health_deductible")}
                    >
                      {t("plan_terms.health_plan_deductible")}
                    </TooltipWithClose>
                  </div>
                  <div className="PlanCard__info_group">
                    <span className="PlanCard__data">
                      {t("om_card.not_applicable")}
                    </span>
                    <TooltipWithClose
                      title={t("plan_card.tooltip.drug_deductible")}
                    >
                      {t("plan_terms.drug_deductible")}
                    </TooltipWithClose>
                  </div>
                  <div className="PlanCard__info_group">
                    <span>
                      <span className="PlanCard__data ds-u-margin-right--1">
                        {t("om_card.no_limit")}
                      </span>
                      <TooltipWithClose
                        title={t("plan_card.tooltip.oopc_maximum")}
                      >
                        {t("plan_terms.oopc_maximum")}
                      </TooltipWithClose>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="PlanCard__flex_right">
              <CollapsibleBox
                labelId="plan_card.view_more_information"
                containerClassname="PlanCard__more_info"
                titleClassname="PlanCard__more_info_trigger"
              >
                <div className="PlanCard__section" data-testid="benefits">
                  <h3 className="PlanCard__section_heading">
                    {t("plan_card.section_heading.plan_benefits")}
                  </h3>
                  <ul>
                    <li>
                      <CheckmarkIcon checked />{" "}
                      {t("plan_terms.accepted_across_us")}
                      {accessibilitySpan(true)}
                    </li>
                    <li>
                      <CheckmarkIcon checked />{" "}
                      {t("om_card.any_doctor_or_hospital")}
                      {accessibilitySpan(true)}
                    </li>
                  </ul>
                </div>
                <div className="PlanCard__section" data-testid="copays">
                  <h3 className="PlanCard__section_heading">
                    {t("plan_terms.copays")}
                  </h3>
                  <div>
                    <div className="PlanCard__info_group">
                      <span>{t("plan_terms.primary")}: </span>
                      <span className="ds-u-font-weight--semibold">
                        {t("om_card.twenty_per_visit")}
                      </span>
                    </div>
                    <div className="PlanCard__info_group">
                      <span>{t("plan_terms.specialist")}: </span>
                      <span className="ds-u-font-weight--semibold">
                        {t("om_card.twenty_per_visit")}
                      </span>
                    </div>
                  </div>
                </div>
              </CollapsibleBox>
            </div>
          </div>
        </div>
      </Card>
    </li>
  );
};

export default OriginalMedicarePlanCard;
